import React, { useState, useEffect } from 'react';
import { Save, Eye, FileText, Globe, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface SeoSettings {
  id?: number;
  blockRobots: boolean;
  robotsTxt: string;
  sitemapEnabled: boolean;
  sitemapPriorityHome: number;
  sitemapPriorityPages: number;
  sitemapPriorityVacancies: number;
  sitemapChangefreqHome: string;
  sitemapChangefreqPages: string;
  sitemapChangefreqVacancies: string;
}

interface SitemapPreview {
  home: {
    url: string;
    priority: number;
    changefreq: string;
  };
  pages: Array<{
    url: string;
    name: string;
    priority: number;
    changefreq: string;
    lastmod: string;
  }>;
  vacancies: {
    count: number;
    priority: number;
    changefreq: string;
    exampleUrl: string;
  };
}

const SEOSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<SeoSettings>({
    blockRobots: true,
    robotsTxt: '',
    sitemapEnabled: true,
    sitemapPriorityHome: 1.0,
    sitemapPriorityPages: 0.8,
    sitemapPriorityVacancies: 0.6,
    sitemapChangefreqHome: 'daily',
    sitemapChangefreqPages: 'weekly',
    sitemapChangefreqVacancies: 'daily'
  });
  const [sitemapPreview, setSitemapPreview] = useState<SitemapPreview | null>(null);
  const [showRobotsPreview, setShowRobotsPreview] = useState(false);

  useEffect(() => {
    loadSettings();
    loadSitemapPreview();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/seo-settings');
      if (response.data.success && response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки SEO настроек:', error);
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const loadSitemapPreview = async () => {
    try {
      const response = await api.get('/admin/seo-settings/sitemap-preview');
      if (response.data.success && response.data.preview) {
        setSitemapPreview(response.data.preview);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки превью sitemap:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/admin/seo-settings', settings);
      if (response.data.success) {
        toast.success('SEO настройки сохранены');
        await loadSitemapPreview();
      }
    } catch (error: any) {
      console.error('Ошибка сохранения SEO настроек:', error);
      toast.error(error.response?.data?.message || 'Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const getDefaultRobotsTxt = () => {
    return `User-agent: *
Allow: /
Disallow: /admin/
Disallow: /api/
Sitemap: https://rabotamax.ru/sitemap.xml
`;
  };

  const getRobotsTxtPreview = () => {
    if (settings.blockRobots) {
      return 'User-agent: *\nDisallow: /';
    }
    return settings.robotsTxt || getDefaultRobotsTxt();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SEO Настройки</h1>
          <p className="text-gray-600 mt-1">Управление robots.txt, sitemap.xml и индексацией</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-5 w-5" />
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>

      {/* Блокировка роботов */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Индексация поисковыми роботами</h2>
        </div>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.blockRobots}
              onChange={(e) => setSettings({ ...settings, blockRobots: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">
              Попросить поисковых роботов не индексировать сайт (noindex, nofollow)
            </span>
          </label>
          
          {settings.blockRobots && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
              ⚠️ При включенной блокировке роботов содержимое robots.txt будет автоматически заменено на "User-agent: *\nDisallow: /"
            </p>
            </div>
          )}
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Содержимое robots.txt
            </label>
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => setSettings({ ...settings, robotsTxt: getDefaultRobotsTxt() })}
                className="text-sm text-blue-600 hover:text-blue-700"
                disabled={settings.blockRobots}
              >
                Использовать по умолчанию
              </button>
              <button
                onClick={() => setShowRobotsPreview(!showRobotsPreview)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700"
              >
                <Eye className="h-4 w-4" />
                {showRobotsPreview ? 'Скрыть превью' : 'Показать превью'}
              </button>
            </div>
            {showRobotsPreview && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {getRobotsTxtPreview()}
                </pre>
              </div>
            )}
            <textarea
              value={settings.blockRobots ? 'User-agent: *\nDisallow: /' : (settings.robotsTxt || '')}
              onChange={(e) => {
                if (!settings.blockRobots) {
                  setSettings({ ...settings, robotsTxt: e.target.value });
                }
              }}
              placeholder={getDefaultRobotsTxt()}
              disabled={settings.blockRobots}
              className={`w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                settings.blockRobots ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
            {settings.blockRobots && (
              <p className="mt-2 text-xs text-gray-500">
                Редактирование недоступно при включенной блокировке роботов
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Настройки Sitemap */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Sitemap.xml</h2>
        </div>
        
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.sitemapEnabled}
              onChange={(e) => setSettings({ ...settings, sitemapEnabled: e.target.checked })}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <span className="text-gray-700">Включить sitemap.xml</span>
          </label>

          {settings.sitemapEnabled && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              {/* Приоритеты */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Приоритеты</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Главная страница</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.sitemapPriorityHome}
                      onChange={(e) => setSettings({ ...settings, sitemapPriorityHome: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Обычные страницы</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.sitemapPriorityPages}
                      onChange={(e) => setSettings({ ...settings, sitemapPriorityPages: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Страницы вакансий</label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.sitemapPriorityVacancies}
                      onChange={(e) => setSettings({ ...settings, sitemapPriorityVacancies: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Частота обновления */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Частота обновления</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Главная страница</label>
                    <select
                      value={settings.sitemapChangefreqHome}
                      onChange={(e) => setSettings({ ...settings, sitemapChangefreqHome: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="always">always</option>
                      <option value="hourly">hourly</option>
                      <option value="daily">daily</option>
                      <option value="weekly">weekly</option>
                      <option value="monthly">monthly</option>
                      <option value="yearly">yearly</option>
                      <option value="never">never</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Обычные страницы</label>
                    <select
                      value={settings.sitemapChangefreqPages}
                      onChange={(e) => setSettings({ ...settings, sitemapChangefreqPages: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="always">always</option>
                      <option value="hourly">hourly</option>
                      <option value="daily">daily</option>
                      <option value="weekly">weekly</option>
                      <option value="monthly">monthly</option>
                      <option value="yearly">yearly</option>
                      <option value="never">never</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Страницы вакансий</label>
                    <select
                      value={settings.sitemapChangefreqVacancies}
                      onChange={(e) => setSettings({ ...settings, sitemapChangefreqVacancies: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="always">always</option>
                      <option value="hourly">hourly</option>
                      <option value="daily">daily</option>
                      <option value="weekly">weekly</option>
                      <option value="monthly">monthly</option>
                      <option value="yearly">yearly</option>
                      <option value="never">never</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Превью структуры */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Превью структуры</h3>
                {sitemapPreview && (
                  <div className="space-y-3 text-sm">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">Главная</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {sitemapPreview.home.url}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Priority: {sitemapPreview.home.priority}, 
                        Changefreq: {sitemapPreview.home.changefreq}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">Страницы</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {sitemapPreview.pages.length} страниц
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">Вакансии</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {sitemapPreview.vacancies.count.toLocaleString()} вакансий
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SEOSettingsPage;

